import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import dayjs from 'dayjs';
import { Socket } from 'ngx-socket-io';
import { Subject, takeUntil } from 'rxjs';
import { DonationsEntrySortKey, FindDonationEntryDto } from 'src/app/dtos/donations/find-donation-entry.dto';
import { AoFilterHeaderSettings } from 'src/app/elements/ao-filter-list/ao-filter-list-header.component';
import { FilterPageType } from 'src/app/elements/ao-filter-list/filter-settings/filter-settings.component';
import { DonationEntry, DonationEntryList } from 'src/app/models/donation-entry-list.interface';
import { DonationType } from 'src/app/models/donation.interface';
import { ProjectOverview } from 'src/app/models/projects/project-overview.interface';
import { Tag } from 'src/app/models/tags.model';
import { DonationsService } from 'src/app/services/donations.service';
import { FinTaskDto } from 'src/app/services/fin-api/dto/fin-task.dto';
import { ProjectsService } from 'src/app/services/projects.service';
import { TagsService } from 'src/app/services/tags.service';
import { FormOutType } from 'src/app/utils/form.util';
import { ActionCardType } from './action-card/action-card.component';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit, OnDestroy {
    headerSettings: AoFilterHeaderSettings = {
        defaultLimit: 40,
        fields: [
            {
                placeholder: 'Spender oder Firma',
                name: 'donatorSearch',
                searchType: FormOutType.STRING,
                sortKey: DonationsEntrySortKey.DONATOR_NAME,
                span: 2,
                locked: true,
            },
            {
                placeholder: 'Spendensumme',
                name: 'donationSum',
                searchType: FormOutType.AMOUNT,
                sortKey: DonationsEntrySortKey.AMOUNT,
                locked: true,
            },
            {
                placeholder: 'Zeitraum',
                name: 'date',
                searchType: FormOutType.DATE_RANGE,
                sortKey: DonationsEntrySortKey.DATE,
                locked: true,
            },
            {
                placeholder: 'Konto',
                name: 'channel',
                searchType: FormOutType.STRING,
                sortKey: DonationsEntrySortKey.CHANNEL,
            },
            {
                placeholder: 'Aktion',
                name: 'projects',
                searchType: FormOutType.MULTI_SELECT,
                sortKey: DonationsEntrySortKey.PROJECT,
            },
            {
                placeholder: 'Tags',
                name: 'tags',
                searchType: FormOutType.MULTI_SELECT,
                sortKey: DonationsEntrySortKey.TAGS,
                selectValues: [],
            },
            {
                placeholder: 'Spendenintervall',
                name: 'permanentDonation',
                searchType: FormOutType.STRING,
                selectValues: [
                    { name: 'Alle', value: null },
                    {
                        value: false,
                        name: 'Einzelspenden',
                    },
                    {
                        value: true,
                        name: 'Dauerspenden',
                    },
                ],
            },
            {
                placeholder: 'Spendenart',
                name: 'type',
                searchType: FormOutType.STRING,
                selectValues: [
                    { name: 'Alle', value: null },
                    {
                        value: DonationType.MONEY,
                        name: 'Geldspende',
                    },
                    {
                        value: DonationType.MATERIAL,
                        name: 'Sachspende',
                    },
                    {
                        value: DonationType.ENDOWMENT,
                        name: 'Zustiftung',
                    },
                    {
                        value: DonationType.EXPENSE_WAIVER,
                        name: 'Aufwandsverzicht',
                    },
                ],
            },
            {
                placeholder: 'Zuordnung',
                name: 'hasDonator',
                searchType: FormOutType.STRING,
                sortKey: DonationsEntrySortKey.HAS_DONATOR,
                selectValues: [
                    { name: 'Alle', value: null },
                    {
                        value: true,
                        name: 'Zugeordnet',
                    },
                    {
                        value: false,
                        name: 'Nicht zugeordnet',
                    },
                ],
                span: 0.5,
                locked: true,
            },
            {
                placeholder: 'Geld eingegangen',
                name: 'paid',
                searchType: FormOutType.STRING,
                sortKey: DonationsEntrySortKey.PAYMENT_STATUS,
                selectValues: [
                    { name: 'Alle', value: null },
                    {
                        value: true,
                        name: 'Bezahlt',
                    },
                    {
                        value: false,
                        name: 'Ausstehend',
                    },
                ],
                span: 0.5,
            },
            {
                placeholder: 'Bescheinigung',
                name: 'hasReceipt',
                searchType: FormOutType.STRING,
                sortKey: DonationsEntrySortKey.RECEIPT,
                selectValues: [
                    { name: 'Alle', value: null },
                    {
                        value: true,
                        name: 'Ausgestellt',
                    },
                    {
                        value: false,
                        name: 'Nicht ausgestellt',
                    },
                ],
                locked: true,
            },
            {
                placeholder: 'Spendenzweck',
                name: 'purposeName',
                searchType: FormOutType.STRING,
            },
        ],
    };

    sort: { sortKey: DonationsEntrySortKey; sortType: 'desc' | 'asc' } = {
        sortKey: DonationsEntrySortKey.DATE,
        sortType: 'desc',
    };
    filter: any = {};
    skip = 0;
    limit = 40;
    total = 0;

    spans = this.headerSettings.fields.map((s) => s.span ?? 1);
    visibleSpans = 0;
    _hiddenFilters: string[] = [];
    set hiddenFilters(value: string[]) {
        this._hiddenFilters = value;
        this.visibleSpans = this.headerSettings.fields.reduce(
            (a, c) => (value.some((f) => c.name === f) ? a : a + (c.span ?? 1)),
            0,
        );
    }
    get hiddenFilters(): string[] {
        return this._hiddenFilters;
    }

    activeRequest = false;

    donations: DonationEntry[] = [];
    private readonly donationsSubject = new Subject<DonationEntry[]>();
    $donationsObservable = this.donationsSubject.asObservable();

    actionCardType = ActionCardType;
    filterPageType = FilterPageType;

    private readonly destroy = new Subject<void>();

    constructor(
        private readonly donationsService: DonationsService,
        private readonly tagsService: TagsService,
        private readonly projectsService: ProjectsService,
        private readonly socket: Socket,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        // Einrichtungsassistent (erstmal deaktiviert)

        // this.companiesService.findMine().subscribe((res: Company) => {
        //     if (res.initiated || setupSeen) {
        //         this.getData();
        //     } else {
        //         this.router.navigate(['/setup']);
        //     }
        // });

        this.projectsService.findAll({}).subscribe((res: ProjectOverview[]) => {
            this.headerSettings.fields[4].selectValues = res.map((t) => ({ name: t.name, value: t._id }));
        });

        this.tagsService.find({}).subscribe((res: Tag[]) => {
            this.headerSettings.fields[5].selectValues = res.map((t) => ({ name: t.name, value: t._id }));
        });

        const filter = sessionStorage.getItem('filter-donations');
        if (filter) {
            this.filter = JSON.parse(filter);
        }

        this.getData();

        this.socket
            .fromEvent<FinTaskDto & { amount: number }>('fin')
            .pipe(takeUntil(this.destroy))
            .subscribe((res) => {
                if (res.status === 'COMPLETED') {
                    this.skip = 0;
                    this.getData();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy.next();
        this.destroy.complete();
    }

    onDelete(id: string): void {
        this.donationsService.deleteOne(id).subscribe(() => {
            this.skip = 0;
            this.getData();
        });
    }

    onScroll(): void {
        if (this.donations.length < this.total && !this.activeRequest) {
            this.activeRequest = true;
            this.getData(true);
        }
    }

    getData(isScroll = false): void {
        this.activeRequest = true;

        const dto: FindDonationEntryDto = this.createDto();

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: dto,
        });

        this.donationsService.findEntry(dto).subscribe((res: DonationEntryList) => {
            this.total = res.total;

            if (isScroll) {
                this.donations = this.donations.concat(res.data);
            } else {
                this.donations = res.data;
            }

            this.donationsSubject.next(this.donations);

            this.skip = this.donations.length;

            this.activeRequest = false;
        });
    }

    createDto(): FindDonationEntryDto {
        const dto: FindDonationEntryDto = {
            sortKey: this.sort.sortKey,
            sortType: this.sort.sortType,
            limit: this.limit,
            skip: this.skip,
            checkAnonymous: true,
        };

        for (const [key, value] of Object.entries(this.filter)) {
            if (value !== undefined && value !== null && value !== 'null') {
                (<any>dto)[key] = value;
            }
        }

        if (this.filter.date) {
            dto.startDate = dayjs(this.filter.date.startDate).toISOString();
            dto.endDate = dayjs(this.filter.date.endDate).toISOString();

            delete (<any>dto).date;
        }

        if (this.filter.donationSum) {
            if (this.filter.donationSum.min) {
                dto.minAmount = this.filter.donationSum.min;
            }

            if (this.filter.donationSum.max) {
                dto.maxAmount = this.filter.donationSum.max;
            }

            delete (<any>dto).donationSum;
        }

        return dto;
    }

    onFilterChange(): void {
        this.skip = 0;
        sessionStorage.setItem('filter-donations', JSON.stringify(this.filter));
        this.getData();
    }
}
